import { FC, useMemo } from 'react'

import { useWallet } from '@solana/wallet-adapter-react'
import classNames from 'classnames'
import { getAdventureStakeUserInfo } from 'fbonds-core/lib/fbond-protocol/functions/banxStaking/banxTokenStaking'
import { chain, first } from 'lodash'

import { staking } from '@banx/api/common'
import { useCountdown } from '@banx/hooks'
import { Info, MoneyBill, Timer } from '@banx/icons'
import { formatCompact, formatNumbersWithCommas, formatTrailingZeros } from '@banx/utils'

import {
  banxTokenBNToFixed,
  getAdventureEndTime,
  getAdventureStatus,
  isAdventureLive,
  isAdventureUpcomming,
} from '../../helpers'
import { AdventureCardHeader, TotalParticipationColumn } from './components'

import styles from './AdventuresContent.module.less'

interface AdventuresListProps {
  banxStakeInfo: staking.BanxStakingInfo
  className?: string
}

export const AdventuresList: FC<AdventuresListProps> = ({ banxStakeInfo, className }) => {
  const { adventures, subscription } = banxStakeInfo

  const visibleAdventures = useMemo(() => {
    const ongoingAdventures = chain(adventures)
      .filter((adventure) => isAdventureLive(adventure) || isAdventureUpcomming(adventure))
      .value()

    const currentAdventure = first(ongoingAdventures)
    if (!currentAdventure) return []

    const nextScheduledAdventure = chain(adventures)
      .filter((adventure) => adventure.week.gt(currentAdventure.week))
      .sortBy((adventure) => adventure.week.toNumber())
      .first()
      .value()

    return nextScheduledAdventure ? [currentAdventure, nextScheduledAdventure] : [currentAdventure]
  }, [adventures])

  return (
    <ul className={classNames(styles.list, className)}>
      {visibleAdventures.map((adventure) => (
        <AdventureCard
          key={adventure.publicKey.toBase58()}
          adventure={adventure}
          subscription={subscription ?? undefined}
        />
      ))}
    </ul>
  )
}

interface AdventureCardProps {
  adventure: staking.BanxAdventure
  subscription?: staking.BanxAdventureSubscription
}

const AdventureCard: FC<AdventureCardProps> = ({ adventure, subscription }) => {
  const { connected } = useWallet()
  const isSubscribed = !!subscription

  return (
    <li className={styles.card}>
      <AdventureCardHeader adventure={adventure} />

      <div className={styles.info}>
        <AdventuresTimer adventure={adventure} />

        <div className={styles.stats}>
          <TotalParticipationColumn adventure={adventure} />

          {connected && isSubscribed && (
            <WalletParticipationColumn adventure={adventure} subscription={subscription!} />
          )}

          {connected && !isSubscribed && <NotParticipatedColumn />}
        </div>
      </div>
    </li>
  )
}

export const NotParticipatedColumn = () => {
  return (
    <div className={styles.statsColWarn}>
      <Info />
      <p>Stake your NFTs or $BANX tokens to participate</p>
    </div>
  )
}

type WalletParticipationColumnProps = {
  adventure: staking.BanxAdventure
  subscription: staking.BanxAdventureSubscription
}

export const WalletParticipationColumn: FC<WalletParticipationColumnProps> = ({
  adventure,
  subscription,
}) => {
  const TITLE_BY_STATUS: Record<staking.AdventureStatus, string> = {
    [staking.AdventureStatus.ENDED]: 'You participated with',
    [staking.AdventureStatus.LIVE]: 'You are participating with',
    [staking.AdventureStatus.UPCOMING]: 'You subscribed with',
  }

  const adventureStatus = getAdventureStatus(adventure)

  const adventureStats = getAdventureStakeUserInfo({ adventure, subscription })
  const { partnerPointsStaked, tokensStaked, nftsStaked } = adventureStats

  //? Formatting values
  const formattedNftsStaked = formatNumbersWithCommas(nftsStaked.toNumber())
  const formattedTokensStaked = formatCompact(banxTokenBNToFixed(tokensStaked))
  const formattedPartnerPoints = formatTrailingZeros(
    formatNumbersWithCommas(partnerPointsStaked.toString()),
  )

  return (
    <div className={styles.statsCol}>
      <h5>{TITLE_BY_STATUS[adventureStatus]}</h5>
      <p>{formattedNftsStaked} Banx</p>
      <p>{formattedTokensStaked} $BANX</p>
      <p>{formattedPartnerPoints} Partner pts</p>
    </div>
  )
}

type AdventuresTimerProps = {
  adventure: staking.BanxAdventure
}

const AdventuresTimer: FC<AdventuresTimerProps> = ({ adventure }) => {
  const TIMER_TITLE_BY_STATUS: Record<staking.AdventureStatus, string> = {
    [staking.AdventureStatus.LIVE]: 'Before rewards distribution',
    [staking.AdventureStatus.UPCOMING]: 'Before next week',
    [staking.AdventureStatus.ENDED]: '',
  }

  const status = getAdventureStatus(adventure)
  const timerTitle = TIMER_TITLE_BY_STATUS[status]

  const endTime = getAdventureEndTime(adventure)
  const timeLeft = useCountdown(endTime)

  const isLive = isAdventureLive(adventure)

  const formattedCountdown = `${timeLeft.days}d : ${timeLeft.hours}h : ${timeLeft.minutes}m`

  return (
    <div className={styles.timerWrapper}>
      <div className={styles.timerIcon}>
        {isLive ? <MoneyBill /> : <Timer className={styles.timerSvg} />}
      </div>
      <div>
        <span className={styles.timer}>{formattedCountdown}</span>
        <p className={styles.timerText}>{timerTitle}</p>
      </div>
    </div>
  )
}
